import { Pipe, PipeTransform } from '@angular/core';

const PROXY_BASE_URL = 'https://img.noodl.ist';
const DEFAULT_DENSITIES = [1, 2] as const;

export interface ImageProxySrcSetOptions {
  width: number;
  height: number;
  format?: string;
  quality?: number;
  densities?: readonly number[];
}

@Pipe({
  name: 'imageProxySrcSet',
  standalone: true,
})
export class ImageProxySrcSetPipe implements PipeTransform {
  transform(filename: string, options: ImageProxySrcSetOptions): string {
    if (!filename) {
      throw new Error('ImageProxySrcSetPipe requires a filename to transform.');
    }

    if (!options) {
      throw new Error('ImageProxySrcSetPipe requires an options object.');
    }

    const {
      width,
      height,
      format,
      quality,
      densities = DEFAULT_DENSITIES,
    } = options;

    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      throw new Error('ImageProxySrcSetPipe requires width and height to be positive finite numbers.');
    }

    if (quality !== undefined && (!Number.isFinite(quality) || quality < 0 || quality > 100)) {
      throw new Error('ImageProxySrcSetPipe requires quality to be a finite number between 0 and 100.');
    }

    const normalizedFilename = filename.replace(/^\/+/, '').trim();
    if (!normalizedFilename) {
      throw new Error('ImageProxySrcSetPipe requires a filename to transform.');
    }

    const safeFilename = normalizedFilename
      .split('/')
      .filter((segment) => segment.length > 0)
      .map((segment) => {
        try {
          return encodeURIComponent(decodeURIComponent(segment));
        } catch {
          return encodeURIComponent(segment);
        }
      })
      .join('/');

    const cleanDensities = Array.from(
      new Set(
        densities.filter((d) => typeof d === 'number' && Number.isFinite(d) && d > 0)
      )
    ).sort((a, b) => a - b);

    if (cleanDensities.length === 0) {
      throw new Error('ImageProxySrcSetPipe requires at least one valid density.');
    }

    const buildUrl = (targetWidth: number, targetHeight: number): string => {
      const proxyUrl = new URL(`${PROXY_BASE_URL}/${safeFilename}`);
      proxyUrl.searchParams.set('width', String(targetWidth));
      proxyUrl.searchParams.set('height', String(targetHeight));

      if (format) {
        proxyUrl.searchParams.set('format', format);
      }

      if (quality !== undefined) {
        proxyUrl.searchParams.set('quality', String(quality));
      }

      return proxyUrl.toString();
    };

    return cleanDensities
      .map((density) => {
        const targetWidth = Math.ceil(width * density);
        const targetHeight = Math.ceil(height * density);
        return `${buildUrl(targetWidth, targetHeight)} ${density}x`;
      })
      .join(', ');
  }
}
